import React, { useEffect, useRef, useState, useContext } from 'react';

import { PHRPagination } from '@palmhr/palmhr-ui-lib';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import snakeCase from 'lodash/snakeCase';

import { AuthContext } from 'Auth/AuthContext';
import IconButton from 'components/IconButton';
import Search from 'components/Search';
import useDebounce from 'Shared/useDebounce';

import AddNewCustomerButton from './components/AddNewCustomerButton/AddNewCustomerButton';
import CustomersCards from './components/CustomersCards/CustomersCards';
import CustomersTable from './components/CustomersTable/CustomersTable';
import styles from './Customers.module.scss';
import { queryKeys, useQueryCustomers } from '../hooks/useCustomers';
import AddCustomerModal from '../Modal/AddCustomerModal/AddCustomerModal';

const Customers = () => {
  const queryClient = useQueryClient();
  const { authState } = useContext(AuthContext);
  const isAsc = useRef(false);

  const [view, setView] = useState('list');
  const [createWithAccount, setcreateWithAccount] = useState(false);
  const [search, setSearch] = useState(() => sessionStorage.getItem('customerSearch') || '');
  const [addCustomerModal, setAddCustomerModal] = useState(false);

  // Retrieve and parse currentPage from session storage or default to 1
  const initialPage = sessionStorage.getItem('customerPage') || 1;
  const initialPerPage = sessionStorage.getItem('perPage') || 10;
  const [params, setParams] = useState({
    status: 'active',
    currentPage: initialPage,
    perPage: initialPerPage,
    search: '',
    sort: '-started',
  });

  const debouncedSearch = useDebounce(search, 500);
  const isAdmin = authState.user.roles.includes('ROLE_ADMIN');
  const isClientsManager = authState.user.roles.includes('ROLE_CLIENTS_MANAGER');

  useEffect(() => {
    setParams((prevState) => ({ ...prevState, currentPage: initialPage, search: debouncedSearch }));
  }, [debouncedSearch]);

  useEffect(() => {
    sessionStorage.setItem('customerSearch', search);
  }, [search]);

  useEffect(() => {
    sessionStorage.setItem('customerPage', params.currentPage);
  }, [params.currentPage]);

  useEffect(() => {
    sessionStorage.setItem('perPage', params.perPage);
  }, [params.perPage]);

  const { isLoading: isLoadingCustomers, data } = useQueryCustomers({
    ...params,
    status: snakeCase(params.status),
  });

  const customersData = data?.data?.items || [];

  const showSearch =
    isLoadingCustomers ||
    (!search && customersData.length > 0) ||
    (search && customersData.length === 0) ||
    (search && customersData.length > 0);

  const onSort = (option) => {
    isAsc.current = !isAsc.current;
    const dir = isAsc.current ? '+' : '-';
    setParams((prevState) => ({ ...prevState, currentPage: 1, sort: `${dir}${option}` }));
  };

  const updateList = () => {
    queryClient.invalidateQueries(queryKeys.customers(params));
  };

  const updatePaginationParams = ({ currentPage, perPage }) => {
    setParams((prevState) => ({ ...prevState, currentPage, perPage }));
  };

  const toggleView = () => {
    setView(view === 'list' ? 'grid' : 'list');
  };
  const OpenModal = () => {
    setAddCustomerModal(true);
  };

  const onDropMenuSelect = (item) => {
    if (item === 'withAccount') {
      setcreateWithAccount(true);
    } else if (item === 'withoutAccount') {
      setcreateWithAccount(false);
    }
    OpenModal();
  };

  return (
    <div className='mt-3 mx-3'>
      <div className={cx(styles.wrapper, 'panel p-0')}>
        <div className='px-5 py-4 border-btm d-flex align-items-center justify-content-between'>
          <div>
            <h2>Customers</h2>
            <p className='tab-subtitle'>List of all customers and their details</p>
          </div>
          {(isAdmin || isClientsManager) && (
            <div className={cx(styles.btnGroup, 'col-4 p-0')}>
              <AddNewCustomerButton onAdd={onDropMenuSelect} />
            </div>
          )}
        </div>
        {showSearch && (
          <div className='d-flex mb-2 mt-2 flex-row align-items-center justify-content-between px-5'>
            <div className='col-4 p-2'>
              <Search
                className='p-0'
                onChange={(e) => {
                  setSearch(e.target.value.replace(/^\s+/g, ''));
                  sessionStorage.removeItem('customerPage');
                }}
                placeholder='Search customer'
                value={search}
                onClear={() => {
                  setSearch('');
                }}
              />
            </div>
            <IconButton size='md' icon={view === 'list' ? 'icon-grid' : 'icon-grid-row-3'} onClick={toggleView} />
          </div>
        )}
        <div style={{ height: 'calc(100vh - 190px)' }} className='mx-5'>
          {view === 'list' ? (
            <div className='h-100 d-flex justify-content-start flex-column gap-2'>
              <CustomersTable
                onSort={onSort}
                isLoading={isLoadingCustomers}
                customers={customersData}
                updateList={updateList}
                addNew={onDropMenuSelect}
                withSearch={Boolean(search)}
              />
              {data?.data?.pagination?.total > 0 && (
                <PHRPagination onPaginationAction={updatePaginationParams} pagination={data?.data?.pagination} />
              )}
            </div>
          ) : (
            <CustomersCards
              pagination={data?.data?.pagination}
              updatePaginationParams={updatePaginationParams}
              customersData={customersData}
              isLoading={isLoadingCustomers}
              withSearch={Boolean(search)}
              addNew={OpenModal}
            />
          )}
        </div>
      </div>
      <AddCustomerModal
        isOpen={addCustomerModal}
        createAccount={createWithAccount}
        onClose={() => {
          setAddCustomerModal(false);
        }}
      />
    </div>
  );
};

export default Customers;
